import { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';

import TextInput from '../components/TextInput';
import Typography from '../components/Typography';
import Layout from '../components/Layout';
import TopicList from 'components/TopicList/TopicList';
import useSearchStore from 'stores/SearchStoreV2';
import { EventListWithArrowsAbove } from 'components/EventList/EventList';
import useDonationLanding from 'hooks/useDonationLanding';
import { PopularNonprofitsSection } from 'views/DiscoverView/components';
import withUserContext from 'behaviors/withUserContext';
import useProfileById from 'hooks/useProfileById';
import useSearch from 'hooks/useSearch';
import useLocalFeedStore from 'stores/LocalFeedStore';
import { ReactComponent as SearchIcon } from 'assets/images/icons/search/search-dark.svg';
import { ReactComponent as WalletIcon } from 'assets/images/icons/wallet/wallet.svg';
import { useNavigate } from '@reach/router';

const CauzeAccountButton = ({ onClick }) => {
  // Image asset from Figma (valid for 7 days)
  const imgIcon =
    'https://www.figma.com/api/mcp/asset/1475fe48-e848-4ba7-8575-338ea7d0d13b';

  return (
    <button
      onClick={onClick}
      className="bg-[#181c3f] content-stretch flex gap-[12px] items-center justify-center overflow-clip py-[16px] px-4 relative rounded-[12px] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] w-full border-none cursor-pointer"
      data-name="Button"
      data-node-id="953:32981"
    >
      <div
        className="content-stretch flex flex-col items-start py-[2px] relative shrink-0"
        data-name="Container"
        data-node-id="953:32982"
      >
        <div className="flex items-center justify-center relative shrink-0">
          <div className="-scale-y-100 flex-none">
            <div
              className="h-[28px] relative w-[24.02px]"
              data-name="Icon"
              data-node-id="953:32983"
            >
              <img
                alt="Cauze Account"
                className="block max-w-none size-full"
                src={imgIcon}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col font-['Montserrat:Black',sans-serif] font-black justify-center leading-[0] relative shrink-0 text-[14px] text-center text-white whitespace-nowrap"
        data-node-id="953:32985"
      >
        <p className="leading-[20px]">ADD TO CAUZE ACCOUNT</p>
      </div>
    </button>
  );
};

const AppleWalletButton = ({ onClick }) => {
  // Image asset from Figma (valid for 7 days)
  const imgIcon =
    'https://www.figma.com/api/mcp/asset/bbf4d52e-6e9a-4cfe-b184-8e84f9f3378c';

  return (
    <button
      onClick={onClick}
      className="bg-gradient-to-r from-[#9f55ff] to-[#6830ff] content-stretch flex gap-[11.99px] items-center justify-center overflow-clip py-[16px] px-4 relative rounded-[12px] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] w-full border-none cursor-pointer"
      data-name="Button"
      data-node-id="953:32976"
    >
      <div
        className="content-stretch flex flex-col items-start py-[2px] relative shrink-0"
        data-name="Container"
        data-node-id="953:32977"
      >
        <div className="flex items-center justify-center relative shrink-0">
          <div className="-scale-y-100 flex-none">
            <div
              className="h-[27.988px] relative w-[24.01px]"
              data-name="Icon"
              data-node-id="953:32978"
            >
              <img
                alt="Apple Wallet"
                className="block max-w-none size-full"
                src={imgIcon}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col font-['Montserrat:Black',sans-serif] font-black justify-center leading-[0] relative shrink-0 text-[14px] text-center text-white whitespace-nowrap"
        data-node-id="953:32980"
      >
        <p className="leading-[20px]">ADD TO APPLE WALLET</p>
      </div>
    </button>
  );
};

const GiftedAmount = () => {
  return (
    <div className="flex flex-col gap-y-2 bg-[#E8E4F9] p-6 rounded-[20px] text-center">
      <Typography weight="black" className="text-[42px]">
        $50.00
      </Typography>
      <Typography weight="bold" className="text-[18px]">
        Give this to any cause in the world.
      </Typography>
      <Typography color="secondary" className="text-[14px]">
        (Show the world you give a damn.)
      </Typography>
    </div>
  );
};

const Landing = ({
  donationLandingStore,
  uiStore,
  activeEntity,
  userProfileStore,
}) => {
  const searchStore = useSearchStore();
  useDonationLanding();
  const navigate = useNavigate();

  const userId =
    activeEntity?.userContext?.userId ||
    activeEntity?.userContext?.influencerId;

  useProfileById({
    id: userId,
    userContext: activeEntity?.userContext,
  });
  useSearch({});

  const user = userProfileStore.users.get(+userId);
  const {
    getLocalNonprofits,
    getParsedLocation,
    localEntities,
    parsedLocationString,
  } = useLocalFeedStore();

  const [localLoading, setLocalLoading] = useState(true);
  const [zipCode, setZipCode] = useState(user?.zip);

  const topics = useMemo(() => {
    const _topics = searchStore.topics.map((t) => t.name);
    const _categories = searchStore.categories;

    return [..._topics, ..._categories];
  }, [searchStore.topics, searchStore.categories]);

  useEffect(() => {
    setLocalLoading(true);
    Promise.all(
      zipCode
        ? [
            getParsedLocation({ location: zipCode }),
            getLocalNonprofits({ location: zipCode }),
          ]
        : [],
    ).finally(() => {
      setLocalLoading(false);
    });
  }, [zipCode, getLocalNonprofits]);

  useEffect(() => {
    setZipCode(user?.zip);
  }, [user]);

  return (
    <Layout className="pl-3 pt-8">
      <GiftedAmount />
      <div className="mb-6" />
      <div className="mb-6">
        <TopicList
          title="Trending Topics"
          topics={topics}
          loading={searchStore.loading}
          onClick={() => navigate(`/gift-redeem/trending-topics`)}
        />
      </div>
      <div className="mb-10">
        <EventListWithArrowsAbove
          title="Join Popular Cauzes"
          events={donationLandingStore.topCauzes}
          loading={donationLandingStore.loadingCauzes}
          uiStore={uiStore}
        />
      </div>
      <div className="mb-10">
        <PopularNonprofitsSection
          user={user}
          localEntities={localEntities}
          activeEntity={activeEntity}
          localLoading={localLoading}
          userLoading={user && userProfileStore.loading}
          parsedLocationString={parsedLocationString}
          onApply={(newZip) => {
            setZipCode(newZip);
            userProfileStore.updateZipFromParsedLocation({
              location: newZip,
              id: user?.id,
            });
          }}
        />
      </div>
      <div className="mb-6">
        <TextInput
          placeholder="Search Every Charity in the World"
          icon={<SearchIcon />}
          variant="filled"
        />
      </div>
      <Typography
        color="secondary"
        weight="medium"
        className="mb-6 text-[18px]"
      >
        Not ready to give yet? That's okay...
      </Typography>
      <div className="flex flex-col gap-y-3">
        <AppleWalletButton />
        <CauzeAccountButton />
      </div>
    </Layout>
  );
};

export default withUserContext(
  inject(
    'donationLandingStore',
    'uiStore',
    'userProfileStore',
  )(observer(Landing)),
);
